import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { combineLatest, EMPTY } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { User } from '../../../../classes/user.class';
import { LeaveService } from '../../../../services/leave.service';
import { UserService } from '../../../../services/user/user.service';
import { Utils } from '../../../../utils.class';
import { InputComponent } from '../input/input.component';

@Component({
    selector: 'app-user-with-leave-input',
    standalone: false,
    templateUrl: './user-with-leave-input.component.html',
    styleUrl: './user-with-leave-input.component.scss',
    host: {
        '[class.deleted]': 'isDeleted()',
    },
})
export class UserWithLeaveInputComponent extends InputComponent {
    private userService = inject(UserService);
    private leaveService = inject(LeaveService);

    private users = toSignal(
        combineLatest([
            this.userService.getByType(this.field.dataFilter as any[]),
            this.userService.getList(true),
        ]).pipe(
            map(([filteredUsers, allUsers]) => {
                const currentUser = allUsers.find(u => u.id === this.fc.value);

                return currentUser && !filteredUsers.some(u => u.id === currentUser.id)
                    ? [...filteredUsers, currentUser]
                    : filteredUsers;
            })
        ),
        { initialValue: [] as User[] }
    );

    private userId = toSignal(this.fc.valueChanges, { initialValue: this.fc.value });

    private leaves = toSignal(this.leavesForPlannedDates(), { initialValue: { begin: null, leaves: [] } });

    isRequired = toSignal(this.fc.statusChanges.pipe(map(() => this.fc.hasValidator(Validators.required))), {
        initialValue: this.fc.hasValidator(Validators.required),
    });

    isDeleted = computed(() => this.users().some(u => u.id === this.userId() && !!u.deleted_at));

    employeesWithLeave = computed(() => {
        const users = this.users();
        const { begin, leaves } = this.leaves();
        const dayLeaves = this.leaveService.dayLeaves(leaves, users, begin);

        return users.map(user => ({
            ...user,
            leaves: dayLeaves
                .filter(({ users }) => users.some(u => u.id === user.id))
                .map(({ leave }) => ({
                    id: leave.id,
                    hourtype: leave.hourtype,
                    close_period: leave.close_period,
                    close_day: leave.close_day,
                    begintime: Utils.setTimeByString(leave.begindate, leave.begintime),
                    endtime: Utils.setTimeByString(leave.enddate, leave.endtime),
                })),
        }));
    });

    usersWithLeave = computed(
        () =>
            new Set(
                this.employeesWithLeave()
                    .filter(e => e.leaves.length)
                    .map(e => e.id)
            )
    );

    private leavesForPlannedDates() {
        const fcParent = this.fc.parent;
        const fcMainPhe = this.formData?.mainPhe?.form?.controls?.phe;
        const beginDate = fcParent?.get('begindate') ?? fcMainPhe?.get('begindate');
        const endDate = fcParent?.get('enddate') ?? fcMainPhe?.get('enddate');

        if (!beginDate || !endDate) {
            console.error(
                'UserWithLeaveInputComponent requires begindate and enddate form controls in the parent form group',
                { field: this.field, formData: this.formData, fcParent, fcMainPhe }
            );
            return EMPTY;
        }

        return combineLatest([
            beginDate.valueChanges.pipe(startWith(beginDate.value)),
            endDate.valueChanges.pipe(startWith(endDate.value)),
        ]).pipe(
            debounceTime(50),
            distinctUntilChanged(
                ([prevBegin, prevEnd], [begin, end]) =>
                    Utils.getTimeOrNull(prevBegin) === Utils.getTimeOrNull(begin) &&
                    Utils.getTimeOrNull(prevEnd) === Utils.getTimeOrNull(end)
            ),
            switchMap(([begin, end]) =>
                this.leaveService
                    .getFilteredList(new Date(begin), new Date(end))
                    .pipe(map(leaves => ({ begin, leaves: leaves ?? [] })))
            )
        );
    }
}
